import axios, { AxiosInstance } from "axios";
import { useQuery, UseQueryOptions, QueryKey,useMutation } from "@tanstack/react-query";
import getBITSEndApiMap from "../apiMap";
import { api } from "../services/AxiosService";

// Create a separate axios instance for refresh token that doesn't have auth interceptors
const refreshApi = axios.create({
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface LoginRequest {
  email: string;
  password: string;
}

export interface Permission {
  id: string;
  key: string;
  name: string;
  description: string;
  category: string;
  is_system_permission: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  RolePermission: {
    id: string;
    role_id: string;
    permission_id: string;
    granted_by: string;
    granted_at: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
  };
}

export interface UserRole {
  id: string;
  user_id: string;
  role_id: string;
  assigned_by: string | null;
  assigned_at: string;
  is_primary: boolean;
  context: {
    source: string;
  };
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface BackendRole {
  id: string;
  name: string; // e.g., "admin", "instructor", "student"
  lms_role_reference: string;
  description: string;
  is_system_role: boolean;
  priority: number;
  metadata: {
    scopes: string[];
  };
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  UserRole: UserRole;
  permissions: Permission[];
}

export interface BackendUser {
  id: string;
  name: string;
  email: string;
  profilePicture?: string;
  lastLogin?: string;
  roles: BackendRole[];
}

export interface LoginResponseData {
  accessToken: string;
  expiresIn: string;
  tokenType: string;
  user: BackendUser;
}

export interface LoginResponse {
  isSuccess: boolean;
  message: string;
  data: LoginResponseData;
  user:any
}

export async function loginUser(payload: LoginRequest): Promise<LoginResponse> {
  const url = getBITSEndApiMap("authLogin");
  const { data } = await api.post<LoginResponse>(url, payload);
  return data;
}

export function useLoginQuery(
  payload: LoginRequest,
  options?: Partial<UseQueryOptions<LoginResponse>>
) {

  return useQuery({
    queryKey: ["auth", "login", payload.email] as QueryKey,
    queryFn: () => loginUser(payload),
    enabled: false, // trigger manually with refetch on submit
    retry: 0,
    ...(options as any),
  });
}

export interface LogoutResponse {
  success: boolean;
  message: string;
}

export interface RefreshTokenRole {
  id: string;
  name: string;
  permissions: string[]; // Array of permission keys
}

export interface RefreshTokenUser {
  id: string;
  name: string;
  email: string;
  profilePicture?: string;
  lastLogin?: string;
  roles: RefreshTokenRole[];
}

export interface RefreshTokenResponse {
  success: boolean;
  accessToken: string;
  expiresIn: string;
  tokenType: string;
  user: RefreshTokenUser;
}

export async function refreshToken(): Promise<RefreshTokenResponse> {
  console.log('🔄 AuthAPI: Making refresh token call...');
  const url = getBITSEndApiMap("authRefresh");
  console.log('🔄 AuthAPI: Refresh URL:', url);
  
  try {
    // Get the current token (even if expired) for the refresh call
    const currentToken = localStorage.getItem("auth_token");
    const headers: any = {
      'X-Refresh-Token-Call': 'true' // Mark this as a refresh call
    };
    
    if (currentToken) {
      headers.Authorization = `Bearer ${currentToken}`;
      console.log('🔄 AuthAPI: Using token for refresh:', currentToken.substring(0, 20) + '...');
    }
    
    const { data } = await refreshApi.post<RefreshTokenResponse>(url, {}, { headers });
    console.log('✅ AuthAPI: Refresh token successful:', data);
    return data;
  } catch (error) {
    console.error('❌ AuthAPI: Refresh token failed:', error);
    throw error;
  }
}

export function useRefreshTokenQuery(
  options?: Partial<UseQueryOptions<RefreshTokenResponse>>
) {
  return useQuery({
    queryKey: ["auth", "refresh"] as QueryKey,
    queryFn: refreshToken,
    enabled: false, // trigger manually
    retry: 0,
    ...(options as any),
  });
}

export async function logoutUser(): Promise<LogoutResponse> {
  console.log('🚪 AuthAPI: Making logout call...');
  const url = getBITSEndApiMap("authLogout");
  console.log('🚪 AuthAPI: Logout URL:', url);
  
  try {
    // Get the current token for the logout call
    const currentToken = localStorage.getItem("auth_token");
    const headers: any = {};
    
    if (currentToken) {
      headers.Authorization = `Bearer ${currentToken}`;
      console.log('🚪 AuthAPI: Using token for logout');
    }
    
    const { data } = await refreshApi.post<LogoutResponse>(url, {}, { headers });
    console.log('✅ AuthAPI: Logout successful:', data);
    return data;
  } catch (error) {
    console.error('❌ AuthAPI: Logout failed (continuing anyway):', error);
    // For logout, we don't want to throw the error - we want to continue with local cleanup
    // Return a success response even if the API call fails
    return { success: true, message: 'Logged out locally' };
  }
}

// If needed, could expose a mutation wrapper; for now we'll call logoutUser in context

export interface AuthMeRole {
  id: string;
  name: string; // admin/instructor/student
  lms_role_reference: string;
  description: string;
  is_system_role: boolean;
  priority: number;
  metadata: {
    scopes: string[];
  };
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  UserRole: UserRole;
  permissions: Permission[];
}

export interface AuthMeUser {
  id: string;
  name: string;
  email: string;
  profilePicture?: string;
  lastLogin?: string;
  status?: string;
  preferences?: any;
  roles: AuthMeRole[];
}

export interface AuthMeResponse {
  success: boolean;
  user: AuthMeUser;
}

export async function getAuthMe(): Promise<AuthMeResponse> {
  const url = getBITSEndApiMap("authMe");
  const { data } = await api.get<AuthMeResponse>(url);
  return data;
}

export function useAuthMeQuery(
  options?: Partial<UseQueryOptions<AuthMeResponse>>
) {
  return useQuery({
    queryKey: ["auth", "me"] as QueryKey,
    queryFn: getAuthMe,
    enabled: true,
    retry: 1,
    staleTime: 10_000,
    ...(options as any),
  });
}
